<template>
  <div class="booking-details-modal w-full">
    <!-- Modal content -->
    <div class="relative w-full bg-white border rounded-lg shadow-2xl dark:bg-gray-700">
      <!-- Modal header -->
      <div
        class="flex items-center justify-between p-4 border-b border-gray-200 rounded-t md:p-5 dark:border-gray-600"
      >
        <div>
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
            {{ serviceName }}
          </h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-300">
            {{ timeRange }} - {{ formattedDate }}
          </p>
        </div>
        <button
          type="button"
          class="inline-flex items-center justify-center w-8 h-8 text-sm text-gray-400 bg-transparent rounded-lg hover:bg-gray-200 hover:text-gray-900 ms-auto dark:hover:bg-gray-600 dark:hover:text-white"
          @click="$emit('close')"
        >
          <svg
            class="w-3 h-3"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 14"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
          <span class="sr-only">{{ $t("calendar.auto.close_modal") }}</span>
        </button>
      </div>

      <!-- Capacity Summary -->
      <div class="p-4 bg-gray-50 border-b border-gray-200 dark:bg-gray-800 dark:border-gray-600">
        <div class="flex justify-between text-sm">
          <div class="text-center">
            <p class="text-gray-500 dark:text-gray-400">{{ $t("calendar.auto.capacidad_total") }}</p>
            <p class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ summary?.capacity?.total || 0 }}
            </p>
          </div>
          <div class="text-center">
            <p class="text-gray-500 dark:text-gray-400">{{ $t("calendar.auto.reservados") }}</p>
            <p class="text-lg font-semibold text-eorange-500">
              {{ summary?.total_participants || 0 }}
            </p>
          </div>
          <div class="text-center">
            <p class="text-gray-500 dark:text-gray-400">{{ $t("calendar.auto.disponible") }}</p>
            <p class="text-lg font-semibold text-egreen-500">
              {{ summary?.capacity?.available || 0 }}
            </p>
          </div>
        </div>
      </div>

      <!-- Bookings List -->
      <div class="p-4 space-y-3 max-h-96 overflow-y-auto">
        <div
          v-for="booking in bookings"
          :key="booking.id"
          class="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-600 transition"
        >
          <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div class="min-w-0">
              <p class="font-medium text-gray-900 dark:text-white">
                {{ booking.customer?.name || $t("calendar.auto.cliente") }}
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                {{ booking.itinerary_code || '-' }}
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                {{ $t("calendar.auto.participants_count", { count: booking.participants }) }}
              </p>
            </div>
            <div class="text-left sm:text-right">
              <p class="font-medium text-gray-900 dark:text-white">
                ${{ formatPrice(resolveBookingAmount(booking)) }}
              </p>
              <span
                :class="booking.is_active ? 'text-egreen-600 bg-egreen-100' : 'text-red-600 bg-red-100'"
                class="inline-block px-2 py-0.5 text-xs font-medium rounded-full mt-1"
              >
                {{ booking.is_active ? $t("common.status.active") : $t("common.status.cancelled") }}
              </span>
            </div>
          </div>
          <div
            v-if="booking.is_active !== false"
            class="flex flex-wrap justify-end gap-2 pt-3 mt-3 border-t border-gray-100 dark:border-gray-600"
          >
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-eorange-500 hover:bg-eorange-600"
              @click="$emit('start-chat', booking)"
            >{{ $t("calendar.auto.chatear") }}</button>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-600 dark:text-white dark:border-gray-500"
              @click="$emit('view-booking', booking)"
            >{{ $t("calendar.auto.ver_detalle") }}</button>
          </div>
        </div>

        <div
          v-if="!bookings || bookings.length === 0"
          class="text-center text-gray-500 dark:text-gray-400 py-8"
        >
          <svg
            class="w-12 h-12 mx-auto mb-3 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p>{{ $t("calendar.auto.no_hay_reservas_para_este_horario") }}</p>
        </div>
      </div>

      <!-- Modal footer -->
      <div class="flex items-center justify-end p-4 border-t border-gray-200 rounded-b dark:border-gray-600">
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:bg-gray-600 dark:text-white dark:border-gray-500 dark:hover:bg-gray-700"
          @click="$emit('close')"
        >{{ $t("calendar.auto.cerrar") }}</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BookingDetailsModal',
  props: {
    serviceName: {
      type: String,
      default: '',
    },
    timeRange: {
      type: String,
      default: '',
    },
    formattedDate: {
      type: String,
      default: '',
    },
    bookings: {
      type: Array,
      default: () => [],
    },
    summary: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close', 'start-chat', 'view-booking'],
  methods: {
    resolveBookingAmount(booking) {
      return (
        booking?.provider_facing_amount ??
        booking?.total_amount ??
        booking?.subtotal ??
        booking?.pricing?.total_amount ??
        null
      );
    },
    formatPrice(value) {
      if (value === null || value === undefined) return '0.00';
      return Number(value).toFixed(2);
    },
  },
};
</script>
